import { useEffect } from "react";
import { useTheme } from "@mui/material";
import AuctionHome from "./app/components/AuctionHome";
import HomeBase from "./app/components/HomeBase";
import AuthCallback from "./app/components/AuthCallback";
//import "./index.css"
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { CrtOverlay } from "./app/components/CrtOverlay";
import Auth0ProviderWithHistory from "./app/auth/auth0-provider-with-history";
import AxiosAuthInterceptor from "./app/components/AxiosAuthInterceptor";
import { LandingPage } from "./app/components/nonAuction/LandingPage";
import GamesHome from "./app/components/games/GamesHome";
import { ConfidenceAdminHome } from "./app/components/confidence/admin/AdminHome";
import { TermsOfService } from "./app/components/legal/TermsOfService";
import { PrivacyPolicy } from "./app/components/legal/PrivacyPolicy";
import OverUnderHome from "./app/components/games/OverUnders/OverUnderHome";
import AuctionRosters from "./app/components/AuctionRosters";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch } from "react-redux";
import { synchronizeAuth0WithDbLogin } from "./app/redux/actions/LoginActions";
import { useAppSelector } from "./app/hooks";
import ConfidenceHome from "./app/components/confidence/ConfidenceHome";
import { AuctionTeamSalaryCapsSlab } from "./app/components/AuctionTeamSalaryCapsSlab";
import { OverUnderStandingsSlab } from "./app/components/games/OverUnders/OverUnderStandingsSlab";
import { LoadingScreen } from "./app/components/LoadingScreen";
import { DemoGate } from "./app/components/demo/DemoGate";
import { DemoLayout } from "./app/components/demo/DemoLayout";

function App() {
  return (
    <BrowserRouter>
      <Auth0ProviderWithHistory>
        <AxiosAuthInterceptor>
          <AppRoutes />
        </AxiosAuthInterceptor>
      </Auth0ProviderWithHistory>
    </BrowserRouter>
  );
}

function AppRoutes() {
  const theme = useTheme();
  const { user, isAuthenticated, isLoading } = useAuth0();
  const { authSynchronized } = useAppSelector((state) => state.profile);
  const dispatch = useDispatch();

  // Trigger auth sync when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.sub && !authSynchronized) {
      dispatch(synchronizeAuth0WithDbLogin(user));
    }
  }, [isLoading, isAuthenticated, user, authSynchronized, dispatch]);

  const location = useLocation();
  const crtRoutes = ["/league-home", "/auction", "/rosters"];
  const showCrt = crtRoutes.some((p) => location.pathname.startsWith(p));

  return (
    <div
      className="min-h-screen max-w-screen"
      style={{ backgroundColor: theme.palette.background.default }}
    >
      {showCrt && <CrtOverlay />}
      <Routes>
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/auth-callback" element={<AuthCallback />} />
        <Route path="/" element={<PrivateRoute element={<SmartHome />} />} />
        <Route
          path="/league-home"
          element={<PrivateRoute element={<HomeBase />} />}
        />
        <Route
          path="/auction"
          element={<PrivateRoute element={<AuctionHome />} />}
        />
        <Route
          path="/confidence"
          element={<PrivateRoute element={<ConfidenceHome />} />}
        />
        <Route
          path="/games"
          element={<PrivateRoute element={<GamesHome />} />}
        />
        {/* Public read-only demo — no Auth0. DemoGate seeds a synthetic identity. */}
        <Route
          path="/demo"
          element={
            <DemoGate>
              <DemoLayout />
            </DemoGate>
          }
        >
          <Route index element={<Navigate to="auction" replace />} />
          <Route path="auction" element={<AuctionHome isDemo />} />
          <Route path="confidence" element={<ConfidenceHome isDemo />} />
          <Route path="dashboard" element={<HomeBase isDemo />} />
        </Route>
        <Route
          path="/admin"
          element={<PrivateRoute element={<ConfidenceAdminHome />} />}
        />
        <Route
          path="/over-unders"
          element={<PrivateRoute element={<OverUnderHome />} />}
        />
        <Route
          path="/rosters"
          element={<PrivateRoute element={<AuctionRosters />} />}
        />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
      <AuctionTeamSalaryCapsSlab />
      <OverUnderStandingsSlab />
    </div>
  );
}

export default App;

// Smart Home: redirects to the right place after auth sync.
// - Leagues exist → /league-home
// - No leagues → /games
const SmartHome: React.FC = () => {
  const { owner, authSynchronized } = useAppSelector((state) => state.profile);
  const { isLoading } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || !authSynchronized) return;

    if (!owner?.leagues || owner.leagues.length === 0) {
      navigate("/games", { replace: true });
      return;
    }

    navigate("/league-home", { replace: true });
  }, [isLoading, authSynchronized, owner, navigate]);

  return <LoadingScreen />;
};

const PrivateRoute: React.FC<{ element: React.ReactElement }> = ({
  element,
}) => {
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0();
  const { authSynchronized } = useAppSelector((state) => state.profile);

  if (error) {
    console.error("[PrivateRoute] Auth0 error:", error.message);
    return <LoadingScreen variant="error" />;
  }

  if (isLoading || (isAuthenticated && !authSynchronized)) {
    // console.log("[PrivateRoute] Showing loading spinner...");
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    loginWithRedirect({
      appState: { returnTo: window.location.pathname },
    });
    return <LoadingScreen />;
  }

  return element;
};
